import { type FC } from "react";

import { Box, Flex } from "@mantine/core";
import { Alert, Colors, Control, Spinner, Switch, Tooltip } from "@snapchat/snap-design-system";
import { CheckIcon, Sizes, WarningIcon } from "@snapchat/snap-design-system-icons";
import { clsx } from "clsx";

import { LensCreatorPayoutEligibility, TosKey } from "../../gql-generated/types";
import { BadgeContentLensCreatorPayoutTOSCTA } from "../../lenses/components/list/row/messages.generated";
import { Flexbox } from "../../shared/components";
import { useSetTos, useTos } from "../../shared/hooks";
import { TOP_PERFOMER_TOS_URL, TOP_PERFORMER_LEARN_MORE_LINK_URL } from "../constants";

import styles from "./LensCreatorPayout.module.css";
import {
    CreatorPayoutControlLabel,
    CreatorPayoutControlTooltipLinkText,
    CreatorPayoutControlTooltipText,
    CreatorPayoutSubheader,
    LearnMore,
    OwnerMismatchAlertDescription,
    OwnerMismatchAlertTitle,
    TextOutdatedTosWarning,
} from "./messages.generated";

interface LensCreatorPayoutProps {
    lensCreatorPayoutEnrolled: boolean;
    setLensCreatorPayoutEnrolled: (lensCreatorPayoutEnrolled: boolean) => void;
    lensCreatorPayoutEligibility?: LensCreatorPayoutEligibility;
    /** Distributed user id doesn't match the user id of the logged-in user. */
    ownerMismatch?: boolean;
    /** Used to stop user from unenrolling the lens anywhere where the enrollment is immediately persisted. */
    blockUnenrollment?: boolean;
    /** Lens is within certain number of days from creation */
    lensCreationWithinThreshold?: boolean;
    disabled?: boolean;
    loading?: boolean;
    triggerTosModal: () => void;
    overrideContainerCss?: string;
}

export const LensCreatorPayout: FC<LensCreatorPayoutProps> = ({
    lensCreatorPayoutEnrolled,
    setLensCreatorPayoutEnrolled,
    lensCreatorPayoutEligibility,
    ownerMismatch = false,
    lensCreationWithinThreshold = true,
    disabled = false,
    loading = false,
    triggerTosModal,
    overrideContainerCss,
}) => {
    const { loading: tosLoading, acceptedLatestVersion } = useTos(TosKey.LensCreatorPayoutTos);

    const { loading: setTosLoading } = useSetTos(TosKey.LensCreatorPayoutTos);

    const dataLoading = loading || tosLoading || setTosLoading;
    const isEligible =
        lensCreatorPayoutEligibility === LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityEligible;
    const isPending = lensCreatorPayoutEligibility === LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityPending;

    return (
        <Box className={clsx(styles.container, overrideContainerCss)}>
            <Flexbox alignItems="center" justifyContent="space-between" id="creator-payout-container">
                <div className={styles.controlWrapperStyle}>
                    <Control
                        className={styles.controlStyle}
                        label={
                            <div className="heading-h500">
                                <CreatorPayoutControlLabel />
                                {lensCreatorPayoutEnrolled && !acceptedLatestVersion && (
                                    <Tooltip
                                        content={
                                            <TextOutdatedTosWarning
                                                tosLink={
                                                    <a href={TOP_PERFOMER_TOS_URL} target="_blank" rel="noreferrer">
                                                        <BadgeContentLensCreatorPayoutTOSCTA />
                                                    </a>
                                                }
                                            />
                                        }
                                    >
                                        <WarningIcon
                                            fill={Colors.Orange.V200}
                                            size={Sizes.Large}
                                            style={{ marginRight: 4 }}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        }
                        tooltip={
                            <>
                                <CreatorPayoutControlTooltipText />{" "}
                                <a href={TOP_PERFORMER_LEARN_MORE_LINK_URL} target="_blank" rel="noreferrer">
                                    <CreatorPayoutControlTooltipLinkText />
                                </a>
                            </>
                        }
                        forId="toggle-lens-creator-payout-enrolled"
                    >
                        <CreatorPayoutSubheader
                            learnMoreLink={
                                <a href={TOP_PERFORMER_LEARN_MORE_LINK_URL} target="_blank" rel="noreferrer">
                                    <strong>
                                        <LearnMore />
                                    </strong>
                                </a>
                            }
                        />
                    </Control>
                </div>
                {dataLoading ? (
                    <Spinner className={styles.loadingSpinner} />
                ) : isEligible ? (
                    <Flex bg={Colors.Green.V150} bdrs="100%" w={30} h={30} align="center" justify="center">
                        <CheckIcon fill={Colors.WHITE} size={Sizes.Large} />
                    </Flex>
                ) : (
                    <Switch
                        id="toggle-lens-creator-payout-enrolled"
                        isChecked={lensCreatorPayoutEnrolled || isPending}
                        isDisabled={disabled || ownerMismatch || !lensCreationWithinThreshold || isPending}
                        onClick={acceptedLatestVersion ? setLensCreatorPayoutEnrolled : triggerTosModal}
                    />
                )}
            </Flexbox>
            {(ownerMismatch || !(lensCreationWithinThreshold || lensCreatorPayoutEnrolled)) && (
                <Alert
                    className={styles.ownerMismatchAlert}
                    status="warning"
                    title={<OwnerMismatchAlertTitle />}
                    description={<OwnerMismatchAlertDescription />}
                />
            )}
        </Box>
    );
};
